import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

// Defensive coercion — see useFeedSource.js for why we need this on
// Capacitor iOS. A non-array response from /home-chat would crash the
// chat panel's .map() inside the player overlay.
const asArray = (data) => (Array.isArray(data) ? data : []);

/**
 * useFeedChat — bundles the home chat fetch (10s poll, paused when tab
 * hidden) + the submit + admin-delete handlers. Tightly coupled to the
 * parent's auth/credits prompts: a logged-out submit triggers the auth
 * modal; a low-balance submit triggers the credits prompt.
 *
 * Inputs:
 *   user           — current viewer (for credits / admin checks)
 *   updateCredits  — AuthContext setter, called after a successful post
 *                    so the header balance updates without a refetch
 *   setShowSignInPrompt, setPendingAction — gate the sign-in modal
 *   setShowCreditsPrompt — gates the "top up to keep going" modal
 *
 * Returns: { homeChat, setHomeChat, newHomeChat, setNewHomeChat,
 *            submittingHomeChat, handleSubmitHomeChat, handleDeleteHomeChat }
 */
export function useFeedChat({ user, updateCredits, setShowSignInPrompt, setPendingAction, setShowCreditsPrompt }) {
  const [homeChat, setHomeChat] = useState([]);
  const [newHomeChat, setNewHomeChat] = useState("");
  const [submittingHomeChat, setSubmittingHomeChat] = useState(false);

  // Fetch — initial + 10s poll, paused while tab is hidden.
  useEffect(() => {
    const fetchHomeChat = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await api.get(`/home-chat`);
        setHomeChat(asArray(res.data));
      } catch (err) {
        console.error("Failed to fetch home chat:", err);
      }
    };
    fetchHomeChat();
    const interval = setInterval(fetchHomeChat, 10000);
    const onVisible = () => { if (!document.hidden) fetchHomeChat(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const handleSubmitHomeChat = async (e) => {
    e.preventDefault();
    if (!newHomeChat.trim()) return;
    if (!user) {
      setShowSignInPrompt(true);
      setPendingAction("homeChat");
      return;
    }
    if (user.credits < 0.10) {
      setShowCreditsPrompt("comment");
      return;
    }
    setSubmittingHomeChat(true);
    try {
      const res = await api.post(`/home-chat`, { content: newHomeChat });
      setHomeChat((prev) => [res.data, ...prev]);
      setNewHomeChat("");
      updateCredits(res.data.new_balance);
      toast.success("Message posted!");
    } catch (err) {
      if (err.response?.status === 402) {
        setShowCreditsPrompt("comment");
      } else {
        toast.error(err.response?.data?.detail || "Failed to post message");
      }
    }
    setSubmittingHomeChat(false);
  };

  const handleDeleteHomeChat = async (messageId) => {
    if (!user?.is_admin) {
      toast.error("Admin access required");
      return;
    }
    try {
      await api.delete(`/home-chat/${messageId}`);
      setHomeChat((prev) => prev.filter((msg) => msg.id !== messageId));
      toast.success("Message deleted");
    } catch (err) {
      console.error("Failed to delete message:", err);
      toast.error("Failed to delete message");
    }
  };

  return {
    homeChat, setHomeChat,
    newHomeChat, setNewHomeChat,
    submittingHomeChat,
    handleSubmitHomeChat,
    handleDeleteHomeChat,
  };
}
